import React, { useEffect, useState } from 'react';
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    Grid,
    Paper,
    Snackbar,
    TextField,
    Typography
} from '@mui/material';
import axios from '../../CustomAxios';
import Loading from '../components/Loading';

function CreateProductionReport({ jobId, jobDisplayId, apCompany, setUp, up, plates, onClose, setSuccess, showErrorAlert, setMessage, snackRef, apMachines }) {
    const [state, setState] = useState({
        jobData: {
            job_id: jobId,
            plate_id: '',
            baking_quantity: 0,
            ctp_quantity: 0,
            comment: '',
            unique_id: ''
        },
        plates: [],
        jobs: [],
        maxValues: { maxBake: 0, maxCtp: 0 },
        totals: { totalBake: 0, totalCtp: 0 },
        error: '',
        success: false,
        loader: true,
        selectedMachine: null   // <-- Added: selected machine state
    });
    const [loading, setLoading] = useState(false);
    const [snackbar, setSnackbar] = useState({
        open: false,
        vertical: 'top',
        horizontal: 'center'
    });

    const getPlateDetails = async (tempPlates) => {
        const authData = JSON.parse(localStorage.getItem('AuthData'));
        let rows = [];
        await Promise.all(
            tempPlates.map(async (element) => {
                try {
                    const { data } = await axios.get(
                        `/api/v1/${authData?.type}/plate_inventory/${element.plate_id}/details`,
                        { headers: { Authorization: `Bearer ${authData.access_token}` } }
                    );
                    rows.push({
                        label: `${data?.data?.height}X${data?.data?.width}X${data?.data?.thickness}`,
                        id: element?._id,
                        plate_id: element?.plate_id,
                        bake: element?.bake,
                        ctp: element?.ctp_plate_count
                    });
                } catch (error) {
                    setState(prev => ({ ...prev, error: 'Error fetching plate details' }));
                }
            })
        );
        return rows;
    };

    useEffect(() => {
        const loadPlates = async () => {
            if (plates && plates.length > 0) {
                const plateOptions = await getPlateDetails(plates);
                setState(prev => ({ ...prev, plates: plateOptions }));
            }
        };
        loadPlates();
    }, [plates]);

    const getJobs = async () => {
        try {
            const authData = JSON.parse(localStorage.getItem('AuthData'));
            const { data } = await axios.get(
                `/api/v1/${authData.type}/jobs?ap_company_id=${apCompany}`,
                {
                    headers: { Authorization: `Bearer ${authData.access_token}` }
                }
            );

            const filteredJobs = data.data.data.filter(job =>
                ['ctp', 'delivery'].includes(job.current_status)
            ).map(job => ({
                label: job.job_id,
                id: job._id,
                name: job.name,
                plates: job.plates
            }));

            setState(prev => ({ ...prev, jobs: filteredJobs, loader: false }));
        } catch (error) {
            setState(prev => ({ ...prev, error: 'Error fetching jobs', loader: false }));
        }
    };

    const getTotalDPR = async (uniqueId) => {
        try {
            const authData = JSON.parse(localStorage.getItem('AuthData'));
            const { data } = await axios.get(
                `/api/v1/${authData.type}/production_report/total/${jobId}/${uniqueId}`,
                {
                    headers: { Authorization: `Bearer ${authData.access_token}` }
                }
            );

            setState(prev => ({
                ...prev,
                totals: {
                    totalBake: data.data?.bake || 0,
                    totalCtp: data.data?.ctp || 0
                }
            }));
        } catch (error) {
            setState(prev => ({ ...prev, error: 'Error fetching totals' }));
        }
    };

    useEffect(() => {
        getJobs();
    }, []);

    const handleSubmit = async () => {
        try {
            setLoading(true);
            const authData = JSON.parse(localStorage.getItem('AuthData'));
            
            // Validate plate selection
            if (!state.jobData.plate_id) {
                setState(prev => ({ ...prev, error: 'Please select a plate' }));
                setSnackbar(prev => ({ ...prev, open: true }));
                setLoading(false);
                return;
            }
            
            // Validate machine selection (REQUIRED)
            if (!state.selectedMachine) {
                setState(prev => ({ ...prev, error: 'Please select a machine' }));
                setSnackbar(prev => ({ ...prev, open: true }));
                setLoading(false);
                return;
            }

            const report = {
                job_id: jobId,
                plate: state.jobData.plate_id,
                baking_quantity: state.jobData.baking_quantity,
                ctp_quantity: state.jobData.ctp_quantity,
                comment: state.jobData.comment,
                ap_company: apCompany,
                unique_id: state.jobData.unique_id,
                audit_logs: [{
                    action_by: authData.user_id,
                    timestamp: new Date(),
                    action: "create_dpr"
                }],
                ap_machine: state.selectedMachine._id   // machine ID from selected object
            };

            await axios.post(
                `/api/v1/${authData.type}/production_report`,
                { reports: [report] },
                {
                    headers: { Authorization: `Bearer ${authData.access_token}` }
                }
            );

            setUp(up + 1);
            setState(prev => ({
                ...prev,
                success: true,
                error: '',
                jobData: {
                    ...prev.jobData,
                    baking_quantity: 0,
                    ctp_quantity: 0,
                    comment: ''
                }
            }));
            setSuccess(true);
            snackRef.current.click();
            setMessage("Production Report Created Successfully");
            onClose();
        } catch (error) {
            setState(prev => ({
                ...prev,
                error: error.response?.data?.error || 'Creation failed',
                success: false
            }));
            showErrorAlert("Production Report Creation Failed");
            onClose();
            setSnackbar(prev => ({ ...prev, open: true }));
        } finally {
            setLoading(false);
        }
    };

    return (
        <Card mb={6} sx={{ width: "50vw" }}>
            <CardContent>
                <Paper elevation={3} style={{ padding: "2%", minHeight: "200px" }}>
                    <Typography variant="h5" color="primary" mb={6} gutterBottom>
                        Create Production Report
                    </Typography>
                    <Grid container spacing={3}>
                        <Snackbar
                            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                            open={snackbar.open}
                            onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
                        >
                            <Alert severity={state.error ? 'error' : 'success'}>
                                {state.error || 'Production report created successfully!'}
                            </Alert>
                        </Snackbar>

                        {state.loader ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%' }}>
                                <CircularProgress />
                            </Box>
                        ) : (
                            <>
                                <Grid item xs={12} md={3}>
                                    <TextField
                                        size='small'
                                        label="Job ID"
                                        value={jobDisplayId}
                                        fullWidth
                                        disabled
                                    />
                                </Grid>

                                <Grid item xs={12} md={3}>
                                    <Autocomplete
                                        size='small'
                                        options={state.plates}
                                        getOptionLabel={(option) => option.label}
                                        onChange={(e, newValue) => {
                                            if (newValue) {
                                                setState(prev => ({
                                                    ...prev,
                                                    jobData: {
                                                        ...prev.jobData,
                                                        plate_id: newValue.plate_id,
                                                        unique_id: newValue.id
                                                    },
                                                    maxValues: {
                                                        maxBake: newValue.bake,
                                                        maxCtp: newValue.ctp
                                                    }
                                                }));
                                                getTotalDPR(newValue.id);
                                            }
                                        }}
                                        renderInput={(params) => (
                                            <TextField {...params} label="Select Plate" fullWidth />
                                        )}
                                    />
                                </Grid>

                                <Grid item xs={12} md={3}>
                                    <TextField
                                        size='small'
                                        label="Max Bake"
                                        value={state.maxValues.maxBake}
                                        fullWidth
                                        disabled
                                    />
                                </Grid>

                                <Grid item xs={12} md={3}>
                                    <TextField
                                        size='small'
                                        label="Max CTP"
                                        value={state.maxValues.maxCtp}
                                        fullWidth
                                        disabled
                                    />
                                </Grid>

                                <Grid item xs={12} md={3}>
                                    <TextField
                                        size='small'
                                        label="Total Bake"
                                        value={state.totals.totalBake}
                                        fullWidth
                                        disabled
                                    />
                                </Grid>

                                <Grid item xs={12} md={3}>
                                    <TextField
                                        size='small'
                                        label="Total CTP"
                                        value={state.totals.totalCtp}
                                        fullWidth
                                        disabled
                                    />
                                </Grid>

                                <Grid item xs={12} md={3}>
                                    <TextField
                                        size='small'
                                        label="Bake Quantity"
                                        type="number"
                                        fullWidth
                                        required
                                        value={state.jobData.baking_quantity}
                                        onChange={(e) => {
                                            if (e.target.value > state.maxValues.maxBake) {
                                                setState(prev => ({
                                                    ...prev,
                                                    error: "Please enter a lower amount; you are exceeding the maximum bake quantity",
                                                    success: false
                                                }));
                                                setSnackbar(prev => ({ ...prev, open: true }));
                                                return;
                                            }

                                            if (e.target.value > state.maxValues.maxBake - state.totals.totalBake) {
                                                setState(prev => ({
                                                    ...prev,
                                                    error: "Cannot make entry as the total will exceed max bake quantity",
                                                    success: false
                                                }));
                                                setSnackbar(prev => ({ ...prev, open: true }));
                                                return;
                                            }
                                            setState(prev => ({
                                                ...prev,
                                                jobData: { ...prev.jobData, baking_quantity: parseInt(e.target.value) || 0 }
                                            }));
                                        }}
                                    />
                                </Grid>

                                <Grid item xs={12} md={3}>
                                    <TextField
                                        size='small'
                                        label="CTP Quantity"
                                        type="number"
                                        fullWidth
                                        required
                                        value={state.jobData.ctp_quantity}
                                        onChange={(e) => {
                                            if (e.target.value > state.maxValues.maxCtp) {
                                                setState(prev => ({
                                                    ...prev,
                                                    error: "Please enter a lower amount; you are exceeding the maximum ctp quantity",
                                                    success: false
                                                }));
                                                setSnackbar(prev => ({ ...prev, open: true }));
                                                return;
                                            }

                                            if (e.target.value > state.maxValues.maxCtp - state.totals.totalCtp) {
                                                setState(prev => ({
                                                    ...prev,
                                                    error: "Cannot make entry as the total will exceed max CTP quantity",
                                                    success: false
                                                }));
                                                setSnackbar(prev => ({ ...prev, open: true }));
                                                return;
                                            }
                                            setState(prev => ({
                                                ...prev,
                                                jobData: { ...prev.jobData, ctp_quantity: parseInt(e.target.value) || 0 }
                                            }));
                                        }}
                                    />
                                </Grid>

                                {/* Machine selection - REQUIRED */}
                                <Grid item xs={12} md={3}>
                                    <Autocomplete
                                        size="small"
                                        options={apMachines || []}
                                        getOptionLabel={(option) =>
                                            `${option.name}-${option.serial_no}`
                                        }
                                        value={state.selectedMachine}
                                        onChange={(e, newValue) => {
                                            setState(prev => ({
                                                ...prev,
                                                selectedMachine: newValue,
                                                // Clear machine error when user selects
                                                error: prev.error === 'Please select a machine' ? '' : prev.error
                                            }));
                                        }}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Select Machine *"
                                                fullWidth
                                                required
                                                error={!state.selectedMachine && state.error === 'Please select a machine'}
                                                helperText={!state.selectedMachine && state.error === 'Please select a machine' ? 'Machine is required' : ''}
                                            />
                                        )}
                                    />
                                </Grid>

                                <Grid item xs={12} md={9} lg={9}>
                                    <TextField
                                        size='small'
                                        fullWidth
                                        label="Comments"
                                        value={state.jobData.comment}
                                        onChange={(e) =>
                                            setState(prev => ({
                                                ...prev,
                                                jobData: { ...prev.jobData, comment: e.target.value }
                                            }))
                                        }
                                    />
                                </Grid>

                                <Grid item xs={12} style={{ display: 'flex', justifyContent: 'flex-end', width: '100%', marginTop: 10 }}>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        onClick={handleSubmit}
                                        disabled={loading || !state.selectedMachine}
                                    >
                                        {loading ? "Creating..." : "Create Production Report"}
                                    </Button>
                                </Grid>
                            </>
                        )}
                    </Grid>
                </Paper>
            </CardContent>
        </Card>
    );
}

export default CreateProductionReport;